'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from './ui/AppLogo';

interface AppLayoutProps {
  children: React.ReactNode;
}

const mobileNavItems = [
  { key: 'home', href: '/', label: 'Home', icon: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L10 3l7 6.5V17a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5Z" />
      <path d="M7 18v-6h6v6" />
    </svg>
  )},
  { key: 'history', href: '/qr-history', label: 'My QR Codes', icon: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="10" r="8" />
      <polyline points="10,6 10,10 13,12" />
    </svg>
  )},
  { key: 'analytics', href: '/qr-analytics', label: 'Analytics', icon: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 15L7 10L11 12L17 5" />
      <circle cx="17" cy="5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  )},
  { key: 'campaigns', href: '/campaigns', label: 'Campaigns', icon: (
    <svg width="20" height="20" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="1.5" width="6" height="6" rx="1.2" />
      <rect x="10.5" y="1.5" width="6" height="6" rx="1.2" />
      <rect x="1.5" y="10.5" width="6" height="6" rx="1.2" />
      <path d="M10.5 13.5h6M13.5 10.5v6" />
    </svg>
  )},
  { key: 'links', href: '/shareable-links', label: 'Templates', icon: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.07-7.07L15.66 4.5" />
      <path d="M10 7a5 5 0 0 0-7.07 7.07L4.34 15.5" />
      <line x1="8" y1="12" x2="12" y2="8" />
    </svg>
  )},
  { key: 'settings', href: '/settings', label: 'Settings', icon: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="10" r="3" />
      <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42" />
    </svg>
  )},
];

const desktopNavItems = [
  {
    key: 'nav-generator',
    href: '/',
    label: 'QR Generator',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
        <rect x="11" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
        <rect x="1.5" y="11" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
        <rect x="3.2" y="3.2" width="2" height="2" rx="0.4" fill="currentColor" />
        <rect x="12.8" y="3.2" width="2" height="2" rx="0.4" fill="currentColor" />
        <rect x="3.2" y="12.8" width="2" height="2" rx="0.4" fill="currentColor" />
        <rect x="11" y="11" width="2" height="2" rx="0.4" fill="currentColor" />
        <rect x="15" y="11" width="2" height="2" rx="0.4" fill="currentColor" />
        <rect x="11" y="15" width="2" height="2" rx="0.4" fill="currentColor" />
        <rect x="15" y="15" width="2" height="2" rx="0.4" fill="currentColor" />
      </svg>
    ),
  },
  {
    key: 'nav-history',
    href: '/qr-history',
    label: 'QR History',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="9" r="7.5" />
        <polyline points="9,5 9,9 12,11" />
      </svg>
    ),
  },
  {
    key: 'nav-analytics',
    href: '/qr-analytics',
    label: 'QR Analytics',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 14L6 9L10 11L16 4" />
        <circle cx="16" cy="4" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    key: 'nav-shareable-links',
    href: '/shareable-links',
    label: 'Shareable Links',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11a4 4 0 0 0 5.66-5.66L13.24 3.9" />
        <path d="M9 7a4 4 0 0 0-5.66 5.66L4.76 14.1" />
        <line x1="7" y1="11" x2="11" y2="7" />
      </svg>
    ),
  },
  {
    key: 'nav-campaigns',
    href: '/campaigns',
    label: 'Campaigns',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1.5" y="1.5" width="6" height="6" rx="1.2" />
        <rect x="10.5" y="1.5" width="6" height="6" rx="1.2" />
        <rect x="1.5" y="10.5" width="6" height="6" rx="1.2" />
        <path d="M10.5 13.5h6M13.5 10.5v6" />
      </svg>
    ),
  },
  {
    key: 'nav-admin',
    href: '/admin-panel',
    label: 'Admin Panel',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 1.5L16.5 5.5V9C16.5 13 13 16.2 9 17C5 16.2 1.5 13 1.5 9V5.5L9 1.5Z" />
        <path d="M6.5 9L8.2 10.8L11.5 7.5" />
      </svg>
    ),
  },
  {
    key: 'nav-settings',
    href: '/settings',
    label: 'Settings',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="9" r="3" />
        <path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2M3.7 3.7l1.4 1.4M12.9 12.9l1.4 1.4M3.7 14.3l1.4-1.4M12.9 5.1l1.4-1.4" />
      </svg>
    ),
  },
];

export default function AppLayout({ children }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--background)' }}>

      {/* ── Mobile Menu Overlay ── */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ── Mobile Slide-in Menu ── */}
      <div
        className="fixed top-0 left-0 h-full z-50 lg:hidden transition-transform duration-300 ease-out"
        style={{
          width: '280px',
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          background: 'var(--card)',
          borderRight: '1px solid var(--border)',
          boxShadow: mobileMenuOpen ? '0 0 40px rgba(0,0,0,0.15)' : 'none',
        }}
      >
        {/* Menu Header */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <AppLogo size={36} />
            <div>
              <span className="font-bold text-foreground block" style={{ fontSize: '15px' }}>Nova QR Studio</span>
              <span className="text-muted-foreground" style={{ fontSize: '11px', fontWeight: 500 }}>by NovaMax Infotech</span>
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="btn-ghost p-2 rounded-xl"
            aria-label="Close menu"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="3" x2="15" y2="15" />
              <line x1="15" y1="3" x2="3" y2="15" />
            </svg>
          </button>
        </div>

        {/* Menu Items */}
        <nav className="px-4 py-5 space-y-1">
          {mobileNavItems.map((item) => {
            const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);
            return (
              <Link
                key={item.key}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3.5 px-4 py-3.5 rounded-2xl transition-all duration-200 font-semibold ${
                  isActive
                    ? 'gradient-primary text-white shadow-blue'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
                style={{ fontSize: '15px', WebkitTapHighlightColor: 'transparent' }}
              >
                <span className={isActive ? 'text-white' : 'text-primary'}>{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Menu Footer */}
        <div className="absolute bottom-0 left-0 right-0 px-4 py-5 border-t border-border">
          <div className="gradient-primary-soft rounded-2xl p-3.5">
            <p className="text-foreground font-semibold" style={{ fontSize: '13px' }}>Nova QR Studio</p>
            <p className="text-muted-foreground mt-0.5" style={{ fontSize: '11px' }}>© NovaMax Studios 2025</p>
          </div>
        </div>
      </div>

      {/* ── Desktop Sidebar ── */}
      <aside
        className="hidden lg:flex flex-col fixed left-0 top-0 h-full bg-card border-r border-border z-30 transition-all duration-300 overflow-hidden"
        style={{ width: sidebarCollapsed ? '64px' : '240px' }}
      >
        <div className="flex flex-col h-full">
          <div className={`flex items-center px-4 py-5 border-b border-border ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2 min-w-0">
                <AppLogo size={32} />
                <div className="min-w-0">
                  <span className="font-bold text-sm text-foreground block truncate">Nova QR Studio</span>
                  <span className="text-muted-foreground" style={{ fontSize: '10px', fontWeight: 500 }}>by Novamax Infotech</span>
                </div>
              </div>
            )}
            {sidebarCollapsed && <AppLogo size={32} />}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`btn-ghost p-1.5 rounded-lg hidden lg:flex ${sidebarCollapsed ? 'ml-0' : ''}`}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg
                width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round"
                style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }}
              >
                <path d="M10 3L5 8L10 13" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
            {!sidebarCollapsed && (
              <p className="text-muted-foreground px-3 mb-3" style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Tools
              </p>
            )}
            {desktopNavItems.map((item) => {
              const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  className={`sidebar-link ${isActive ? 'sidebar-link-active' : ''} ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <span className={`flex-shrink-0 ${isActive ? 'text-primary' : ''}`}>{item.icon}</span>
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </Link>
              );
            })}
          </nav>

          {!sidebarCollapsed && (
            <div className="px-4 py-4 border-t border-border">
              <div className="gradient-primary-soft rounded-2xl p-3">
                <p className="text-foreground font-semibold" style={{ fontSize: '12px' }}>Nova QR Studio</p>
                <p className="text-muted-foreground mt-0.5" style={{ fontSize: '11px' }}>© NovaMax Studios</p>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ── Main Content Area ── */}
      <div
        className="flex-1 flex flex-col min-w-0 transition-all duration-300"
        style={{ marginLeft: 0 }}
      >
        {/* Desktop sidebar spacer */}
        <div
          className="hidden lg:block fixed top-0 left-0 h-full pointer-events-none"
          style={{ width: sidebarCollapsed ? '64px' : '240px', transition: 'width 0.3s' }}
        />

        {/* ── Mobile Header ── */}
        <header
          className="lg:hidden flex items-center justify-between sticky top-0 z-30"
          style={{
            padding: '12px 16px',
            background: 'rgba(255,255,255,0.88)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="1" width="4.5" height="4.5" rx="1" fill="white" />
                <rect x="10.5" y="1" width="4.5" height="4.5" rx="1" fill="white" />
                <rect x="1" y="10.5" width="4.5" height="4.5" rx="1" fill="white" />
                <rect x="2.5" y="2.5" width="1.5" height="1.5" rx="0.3" fill="var(--primary)" />
                <rect x="12" y="2.5" width="1.5" height="1.5" rx="0.3" fill="var(--primary)" />
                <rect x="2.5" y="12" width="1.5" height="1.5" rx="0.3" fill="var(--primary)" />
                <rect x="7" y="7" width="2" height="2" rx="0.4" fill="white" />
                <rect x="11" y="7" width="2" height="2" rx="0.4" fill="white" />
                <rect x="7" y="11" width="2" height="2" rx="0.4" fill="white" />
                <rect x="11" y="11" width="2" height="2" rx="0.4" fill="white" />
              </svg>
            </div>
            <span className="font-bold text-foreground" style={{ fontSize: '16px', letterSpacing: '-0.01em' }}>
              Nova QR Studio
            </span>
          </div>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="flex items-center justify-center rounded-xl border border-border bg-secondary"
            style={{ width: '40px', height: '40px', WebkitTapHighlightColor: 'transparent' }}
            aria-label="Open navigation menu"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="2.5" y1="5" x2="15.5" y2="5" />
              <line x1="2.5" y1="9" x2="15.5" y2="9" />
              <line x1="2.5" y1="13" x2="15.5" y2="13" />
            </svg>
          </button>
        </header>

        {/* Page content */}
        <main
          className="flex-1 overflow-auto transition-all duration-300"
          style={{ marginLeft: 0 }}
        >
          <div
            className="lg:transition-all lg:duration-300"
            style={{ marginLeft: 0 }}
          >
            {/* Desktop: offset for sidebar */}
            <div className="hidden lg:block" style={{ marginLeft: sidebarCollapsed ? '64px' : '240px', transition: 'margin-left 0.3s' }}>
              {children}
            </div>
            {/* Mobile: no offset */}
            <div className="lg:hidden">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}